import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark";

interface ThemeState {
  theme: Theme;
  mounted: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setMounted: (mounted: boolean) => void;
}

/**
 * Theme store using Zustand with persistence
 * - Persists theme preference to localStorage
 * - Syncs theme to document class for Tailwind dark mode
 * - Syncs to cookie for SSR theme detection
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: "dark",
      mounted: false,
      setTheme: (theme: Theme) => {
        set({ theme });

        // Apply theme to document immediately
        if (typeof window !== "undefined" && typeof document !== "undefined") {
          if (theme === "light") {
            document.documentElement.classList.remove("dark");
          } else {
            document.documentElement.classList.add("dark");
          }

          // Also sync to cookie for SSR
          syncThemeToCookie(theme);
        }
      },
      toggleTheme: () => {
        const { theme, setTheme } = get();
        setTheme(theme === "light" ? "dark" : "light");
      },
      setMounted: (mounted: boolean) => set({ mounted }),
    }),
    {
      name: "theme-storage",
      partialize: (state) => ({ theme: state.theme }),
    },
  ),
);

/**
 * Sync theme to cookie for server-side rendering
 * This allows the server to read the theme preference from cookies
 */
function syncThemeToCookie(theme: Theme) {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  try {
    // Create the same structure that Zustand persist uses
    const zustandData = { state: { theme }, version: 0 };
    const cookieValue = JSON.stringify(zustandData);

    // Cookie expires in 1 year
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    const cookieString = `theme-storage=${encodeURIComponent(cookieValue)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    // Set cookie silently for SSR theme detection
    document.cookie = cookieString;
  } catch (error) {
    console.warn("Failed to sync theme to cookie:", error);
  }
}
